// ---------------------------------------------------------------------------
// Controller for WorkspaceNav — owns all hooks, store interactions, and
// side-effects. The view receives only the returned props and renders.
// ---------------------------------------------------------------------------
import { ReactNode, useCallback, useEffect, useState } from "react";

import { useQuery } from "@apollo/client";
import { selectWorkspace, useWorkspaceStore } from "stores/workspace/workspaceStore";

import { WORKSPACES, WorkspaceClientName } from "api/workspace";

import { Workspace, WorkspaceQuery, WorkspaceType } from "models/workspace";

import { useUnsavedTypeWellChangesGuard } from "components/arps/UnsavedTypeWellChangesPopconfirm";

export interface WorkspaceOption {
  readonly label: ReactNode;
  readonly value: string;
  readonly type: Maybe<WorkspaceType>;
}

export interface WorkspaceNavControllerResult {
  /** The currently selected workspace (undefined if not yet loaded). */
  readonly workspace: Maybe<Workspace>;
  /** Dropdown options for the workspace select. */
  readonly options: readonly WorkspaceOption[];
  /** Whether the unsaved-changes popconfirm should be visible. */
  readonly isPopconfirmOpen: boolean;
  /** Handler for when the user selects a workspace from the dropdown. */
  readonly onWorkspaceChange: (workspaceId: string) => void;
  /** Handler for confirming a pending workspace change (unsaved changes dialog). */
  readonly onConfirmChange: () => void;
  /** Handler for cancelling a pending workspace change (unsaved changes dialog). */
  readonly onCancelChange: () => void;
}

export function useWorkspaceNavController(): WorkspaceNavControllerResult {
  const workspace = useWorkspaceStore(selectWorkspace);
  const setWorkspaceId = useWorkspaceStore((s) => s.setWorkspaceId);

  const { shouldBlockNavigation } = useUnsavedTypeWellChangesGuard();

  const [options, setOptions] = useState<WorkspaceOption[]>([]);
  const [pendingWorkspaceId, setPendingWorkspaceId] = useState<Maybe<string>>(null);

  const { data, refetch } = useQuery<WorkspaceQuery>(WORKSPACES, {
    context: { clientName: WorkspaceClientName },
    notifyOnNetworkStatusChange: true
  });

  // ── Build dropdown options when workspace list changes ─────────────────
  useEffect(() => {
    if (!data?.workspaces) return;

    // If current workspace no longer exists in the list, switch to Explore or first
    if (
      !data.workspaces.find((w) => w?.workspaceId === workspace?.workspaceId) &&
      workspace
    ) {
      const exploreId = data.workspaces.find((w) => w?.title === "Explore")?.workspaceId;
      const firstId = data.workspaces[0]?.workspaceId;
      setWorkspaceId(exploreId ?? firstId ?? "");
    }

    const nextOptions: WorkspaceOption[] = [];

    data.workspaces.forEach((w) => {
      if (!w) return;
      const { dashboards, title, workspaceId } = w;
      if (!dashboards?.length) return;

      nextOptions.push({
        label: title,
        value: workspaceId,
        type: w.type
      });
    });

    setOptions(nextOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.workspaces]);

  // ── Refetch workspace list if needed ───────────────────────────────────
  useEffect(() => {
    if (workspace && options.length) return;
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace]);

  // ── Handlers ───────────────────────────────────────────────────────────
  const onWorkspaceChange = useCallback(
    (workspaceId: string) => {
      if (shouldBlockNavigation(workspaceId, setPendingWorkspaceId)) {
        return;
      }
      setWorkspaceId(workspaceId);
    },
    [shouldBlockNavigation, setWorkspaceId]
  );

  const onConfirmChange = useCallback(() => {
    if (pendingWorkspaceId) {
      setWorkspaceId(pendingWorkspaceId);
      setPendingWorkspaceId(null);
    }
  }, [pendingWorkspaceId, setWorkspaceId]);

  const onCancelChange = useCallback(() => {
    setPendingWorkspaceId(null);
  }, []);

  return {
    workspace,
    options,
    isPopconfirmOpen: pendingWorkspaceId !== null,
    onWorkspaceChange,
    onConfirmChange,
    onCancelChange
  };
}
